import type { AutomergeUrl } from "@automerge/automerge-repo";
import { useDocument } from "@automerge/react";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { useAppIndex } from "forest/sandbox/use-app-index";
import type { AppIndexEntry } from "playground/apps/index/app-index";
import { useState } from "react";

import BearSandboxProvider, {
  useBearSandbox,
} from "@/components/BearSandboxProvider";
import PaneSuspense from "@/components/PaneSuspense";
import { ProjectContentLoader } from "@/components/ProjectContentLoader";
import { useProjectShellJigName } from "@/components/ProjectShell";
import { JigsProvider } from "@/jigs/JigsProvider";
import { JigView } from "@/jigs/JigView";
import { jigWindow } from "@/jigs/launch";
import { resolveJig } from "@/jigs/routing";
import { JigWindowActionsHost } from "@/jigs/window-actions";
import type { Project } from "@/types/projects";

export const Route = createFileRoute("/p/$projectId/$branch/$scope/$appId")({
  pendingMs: 0,
  pendingComponent: ProjectContentLoader,
  component: JigRoute,
});

function JigRoute() {
  const { projectId, branch, scope, appId } = Route.useParams();
  const { indexUrl: mainIndexUrl } =
    getRouteApi("/p/$projectId").useLoaderData();
  const { task } = getRouteApi("/p/$projectId/$branch").useLoaderData();
  const indexUrl = (task?.branch_url ?? mainIndexUrl) as AutomergeUrl;
  const [project] = useDocument<Project>(indexUrl, { suspense: true });
  return (
    <BearSandboxProvider
      key={`${projectId}:${branch}:${scope}/${appId}`}
      projectId={projectId}
      project={project}
      indexUrl={indexUrl}
      taskId={task?.id}
    >
      <JigsProvider projectId={projectId}>
        <JigWindow />
      </JigsProvider>
    </BearSandboxProvider>
  );
}

function JigWindow() {
  const { scope, appId } = Route.useParams();
  const { playground } = useBearSandbox();
  const { entries, loaded } = useAppIndex(playground);
  const entry = resolveJig(entries, scope, appId);
  if (!loaded) return <ProjectContentLoader />;
  if (!entry?.manifest)
    return (
      <p role="alert" className="p-6 text-sm text-muted-foreground">
        This jig is not installed in this project.
      </p>
    );
  if (!jigWindow(entry))
    return (
      <p role="alert" className="p-6 text-sm text-muted-foreground">
        This jig does not have a supported window.
      </p>
    );
  return <InstalledJigWindow entry={entry} />;
}

function InstalledJigWindow({ entry }: { entry: AppIndexEntry }) {
  useProjectShellJigName(entry.displayName);
  const [actions, setActions] = useState<HTMLDivElement | null>(null);
  return (
    <section
      data-testid="jig-window"
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <header className="flex items-center gap-2 border-b px-6 py-3">
        <h1 className="text-lg font-semibold">{entry.displayName}</h1>
        <div ref={setActions} className="ml-auto flex items-center gap-2" />
      </header>
      {actions && (
        <JigWindowActionsHost value={actions}>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <PaneSuspense>
              <JigView entry={entry} />
            </PaneSuspense>
          </div>
        </JigWindowActionsHost>
      )}
    </section>
  );
}
